import { fetchNHSTagContent } from 'src/pages/tag/actions';
import actionTypes from './actionTypes';
import { trackFootfallEvent } from 'src/utils/footfall';
import EventType from 'src/constants/footfallEventTypes';
import { removeRecipientFromUrl } from 'src/utils/router';
import ModalType from 'src/constants/modalTypes';
import Router from 'next/router';
import { Section, signupContextType } from 'src/constants/signupContext';
import { Community, State } from 'src/types';

type ModalProps = {
  [key: string]: unknown;
}

export function showModal({
  modalType = ModalType.DEFAULT,
  modalProps = {},
}: {modalType?: ModalType, modalProps?: ModalProps}) {
  return { type: actionTypes.MODAL_SET, modalType, modalProps };
}

export function setModalProps(modalProps: ModalProps) {
  return { type: actionTypes.MODAL_SET_PROPS, modalProps };
}

export function closeModal() {
  return { type: actionTypes.MODAL_UNSET };
}

export function showSignupModal(
  signupContext: signupContextType | null = {section: Section.UNTRACKED},
  group: Community | null = null,
  noLoginClose?: boolean,
  defaultEmail = '',
  onSignupSuccess: () => void = () => {},
  redirectPage?: () => void,
) {
  return showModal({
    modalType: ModalType.SIGN_UP,
    modalProps: {
      signupContext,
      group,
      noLoginClose,
      defaultEmail,
      onSignupSuccess,
      redirectPage,
    },
  });
}

// @ts-expect-error TODO: Add types
export function showHuLocalSignupModal(signupContext, defaultEmail, suggestedCommunities) {
  return showModal({
    modalType: ModalType.HULOCAL_SIGN_UP,
    modalProps: {
      signupContext,
      defaultEmail,
      suggestedCommunities,
    },
  });
}

type ShowLoginModal = {
  noClose?: boolean;
  onLoginSuccess?: () => void;
  username?: string;
  clickMetaData?: {[key:string]: string};
}

export function showLoginModal({
  noClose = false,
  onLoginSuccess,
  username = '',
  clickMetaData,
}: ShowLoginModal = {}) {
  trackFootfallEvent(EventType.CLICKED, { clickType: 'login-modal-viewed', clickMetaData });

  return showModal({
    modalType: ModalType.LOGIN,
    modalProps: {
      noClose,
      onLoginSuccess,
      username,
    },
  });
}

export function showInvalidLoginModal() {
  return showModal({
    modalProps: {
      size: 'sm',
      children: 'Invalid login!',
    },
  });
}

// @ts-expect-error TODO: Add types
export function showSignupSocialModal(authdata, onSuccess, surveyProps) {
  // Explicit facebook DOB
  if (authdata.birthday) {
    const birthday = authdata.birthday.split('/');
    authdata.dateOfBirth = birthday[2] + '/' + birthday[0] + '/' + birthday[1];
  }
  return showModal({
    modalType: ModalType.SIGN_UP_SOCIAL,
    modalProps: {
      authdata,
      onSuccess,
      surveyProps,
    },
  });
}

// @ts-expect-error TODO: Add types
export function showExistingLoginSocialModal(authdata, onSuccess) {
  return showModal({
    modalType: ModalType.EXISTING_LOGIN_SOCIAL,
    modalProps: {
      authdata,
      onSuccess,
    },
  });
}

export function showDFPModal(content: unknown) {
  return (dispatch: any, getState: () => State) => {
    // BB-1274: Prevent Google Ad Manager (replaces DFP) popups from overwriting our Sign up Modal
    const { modal: { modalType }} = getState();
    if (modalType) return;

    return dispatch(showModal({
      modalType: ModalType.DFP,
      modalProps: { content },
    }));
  };
}

export function showConfirmModal(modalProps: ModalProps) {
  return showModal({ modalType: ModalType.CONFIRM, modalProps });
}

export function showTakeProfileSurveyModal() {
  trackFootfallEvent(EventType.PROFILE_SURVEY, { clickType: 'survey-param' });
  return showModal({ modalType: ModalType.TAKE_PROFILE_SURVEY });
}

const HIDE_MODAL_CLOSE_BUTTON = '0';
const OPEN_SIGNUP_NORMALLY = '1';
const INACTIVE_USER_SURVEY = '2';
const SURVEY = '3';
const COVID_19_FEATURED_POST_MODAL = '19';
const PROFILE_SURVEY = 'profile';

// @ts-expect-error TODO: Add types
export const handleQueryParams = (queryParams, footfallSection = '') => {
  return async (dispatch: any, getState: () => State) => {
    if (!queryParams) return;
    // To fix: URLs with hash don't have the params below so opening the welcome back modal doesn't work
    const { utm_source, popup, exit, collector, recipient, survey } = queryParams;
    const recipientParam = recipient;
    const { context: { currentUser, currentCommunity } } = getState();
    // exception, other modals take priority, we prevent double footfall events by checking the route
    const isEditProfilePage = Router.route === '/user/editProfile';

    if (popup === COVID_19_FEATURED_POST_MODAL) {
      await dispatch(fetchNHSTagContent('coronavirus-covid-19', 'conditions'));
      dispatch(showModal({
        modalType: ModalType.NHS_TAG_CONTENT,
        modalProps: {
          tagName: 'Coronavirus (COVID-19)',
        },
      }));
      return;
    }

    if (popup === INACTIVE_USER_SURVEY) {
      dispatch(showModal({
        modalType: ModalType.IFRAME,
        modalProps: {
          iframe: {
            src: `https://www.smartsurvey.co.uk/s/O6STRR/?id=${recipient}`,
            height: '605px',
          },
        },
      }));
      return;
    }

    if (popup === SURVEY) {
      dispatch(showModal({
        modalType: ModalType.IFRAME,
        modalProps: {
          iframe: {
            src: `https://surveys.healthunlocked.com/s/${collector}/?id=${recipient}`,
            height: '605px',
          },
        },
      }));
      return;
    }

    if (currentUser) {
      if (recipientParam) removeRecipientFromUrl();

      // popup=profile used in featured posts, survey=true in emails
      if ((survey === 'true' || popup === PROFILE_SURVEY) && !isEditProfilePage) {
        dispatch(showTakeProfileSurveyModal());
        return;
      }

      return;
    }

    // Only for visitors
    if (utm_source === 'notification') {
      dispatch(showLoginModal({ noClose: true, username: recipientParam }));
      if (recipientParam) {
        // delay removing param to make sure we display username in login modal
        setTimeout(() => removeRecipientFromUrl(), 1500);
      }
    } else if (popup === OPEN_SIGNUP_NORMALLY) {
      const hideClose = exit === HIDE_MODAL_CLOSE_BUTTON;
      trackFootfallEvent(EventType.CLICKED, { clickType: 'sign-up', clickMetadata: { section: footfallSection}});
      dispatch(showSignupModal(null, currentCommunity || null, hideClose));
    }
  };
};
